import { JRCButton } from 'components/JRCButton/JRCButton';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { JRCInputCheckbox } from '../JRCInputCheckbox/JRCInputCheckbox';
import * as Components from './JRCInputSelect.style';
import { useForm } from 'react-hook-form';
import { PagingResults, jObjectAutocomplete, deepEqual } from 'jamespot-user-api';
import { JRCFlexBox } from 'components/JRCFlex/JRCFlexBox';
import { JRCModalButtonType } from '../../../JRCModal/JRCModalContent';
import { JRCModal } from '../../../JRCModal/templates/JRCModal';
import { JRCMessage } from '../../../JRCMessage/JRCMessage';

type SelectHierarchicalTreeModalProps = {
    hierarchicalTaxonomyPromise: (uriConcept: string) => Promise<PagingResults<jObjectAutocomplete>>;
    close: () => void;
    inPlace: boolean;
    options: any[];
    updateOptions: (options: any[]) => void;
    limit?: number;
};

export type SKOSTree = {
    title: string;
    uri: string;
};

const SelectHierarchicalTreeModal: React.FC<SelectHierarchicalTreeModalProps> = ({
    hierarchicalTaxonomyPromise,
    close,
    inPlace,
    options,
    updateOptions,
    limit,
}) => {
    const intl = useIntl();

    const [isLoading, setIsLoading] = React.useState(false);
    const [hierarchicalData, setHierarchicalData] = React.useState<any>([]);
    const [skosTree, setSkosTree] = React.useState<SKOSTree[]>([
        { title: intl.formatMessage({ id: 'HOMEUSER_Home' }), uri: '' },
    ]);
    const [hierarchy, setHierarchy] = React.useState<SKOSTree>();

    const formEdit = useForm({
        defaultValues: {
            options: options,
        },
        criteriaMode: 'all',
    });
    const editOptions = formEdit.watch('options');

    const ModalButtons: JRCModalButtonType[] = [
        {
            color: 'cancel',
            clickHandler: close,
            children: <FormattedMessage id="GLOBAL_Cancel" />,
        },
        {
            color: 'valid',
            children: <FormattedMessage id="GLOBAL_Save" />,
            clickHandler: async () => {
                updateOptions(editOptions);
            },
        },
    ];

    const hierarchicalTaxonomyFunction = React.useCallback(
        (uriConcept: string, title?: string) => {
            if (hierarchicalTaxonomyPromise) {
                setIsLoading(true);
                hierarchicalTaxonomyPromise(uriConcept)
                    .then((response) => {
                        const dataTemp = response.data.map((d) => {
                            return {
                                ...d,
                                label: (
                                    <Components.CheckboxLabelButton
                                        onClick={() => {
                                            hierarchicalTaxonomyFunction(d.uri, d.title);
                                        }}>
                                        {d.title}
                                        <JRCIcon name="icon-angle-right" size={12} color="secondary" />
                                    </Components.CheckboxLabelButton>
                                ),
                                value: { ...d },
                            };
                        });
                        if (!!uriConcept && !!title) {
                            setHierarchy({ uri: uriConcept, title: title });
                        }
                        setHierarchicalData(dataTemp);
                        setIsLoading(false);
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            }
        },
        [hierarchicalTaxonomyPromise],
    );

    React.useEffect(() => {
        if (hierarchy) {
            const index = skosTree.findIndex((skos: SKOSTree) => skos.uri == hierarchy?.uri);
            if (index === -1) {
                setSkosTree([...skosTree, { uri: hierarchy.uri, title: hierarchy.title }]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hierarchy]);

    React.useEffect(() => {
        hierarchicalTaxonomyFunction('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <JRCModal
            open={true}
            closeHandler={close}
            portalId="react-modal"
            inPlace={inPlace}
            title={intl.formatMessage({ id: 'GLOBAL_CHECK_BOXES' })}
            buttons={ModalButtons}>
            <Components.InputSelectHierarchySearch>
                <Components.ModalContentInputSelectList>
                    <Components.InputSelectCheckboxWrapper>
                        <Components.FlexBoxTreeDescription>
                            {intl.formatMessage({ id: 'HIERARCHICAL_TAXONOMY_DESCRIPTION' })}
                        </Components.FlexBoxTreeDescription>
                        {editOptions.length === limit && (
                            <JRCMessage
                                variant={'warning'}
                                message={intl.formatMessage({ id: 'INPUT_SELECT_Has_Reached_Limit' }, { limit })}
                            />
                        )}
                        <Components.SelectedOptionWrapper>
                            {editOptions?.map((option: any, index: number) => (
                                <Components.SelectedOption
                                    key={`${option.title.replace(/[^a-z0-9]/gi, '')}-${index}`}
                                    onClick={() =>
                                        formEdit.setValue(
                                            'options',
                                            editOptions.filter((initialValue) => !deepEqual(initialValue, option)),
                                        )
                                    }>
                                    <Components.JRCDefaultSelectedOption>
                                        <Components.TimesIcon />
                                        {option.title}
                                    </Components.JRCDefaultSelectedOption>
                                </Components.SelectedOption>
                            ))}
                        </Components.SelectedOptionWrapper>
                        <JRCFlexBox direction="row" x="flex-start" y="flex-start">
                            {skosTree?.length &&
                                skosTree.map((skos: { uri: string; title: string }, index: number) => (
                                    <Components.CheckboxLabelButton
                                        key={index}
                                        onClick={() => {
                                            const skosTreeTemp = [...skosTree];
                                            const index = skosTreeTemp.findIndex((s: SKOSTree) => s.uri == skos.uri);
                                            if (index !== -1) {
                                                setSkosTree((s) => s.slice(0, index + 1));
                                            }
                                            hierarchicalTaxonomyFunction(skos.uri);
                                        }}>
                                        {skos.title}
                                        <JRCIcon name="icon-angle-right" size={12} color="secondary" />
                                    </Components.CheckboxLabelButton>
                                ))}
                        </JRCFlexBox>
                        {isLoading ? (
                            <JRCLoader size="m" />
                        ) : hierarchicalData.length ? (
                            <JRCInputCheckbox
                                label=""
                                description=""
                                name="options"
                                control={formEdit.control}
                                options={hierarchicalData}
                                direction="column"
                                labelPosition="before"
                                checkboxMode="checkbox"
                                limit={limit}
                            />
                        ) : (
                            <Components.NoResult>
                                <JRCIcon name="icon-tag" size={60} />
                                <JRCTypography size="l" variant="p">
                                    {intl.formatMessage({ id: 'GLOBAL_No_Result' })}
                                </JRCTypography>
                                <JRCButton
                                    onClick={() => {
                                        const skos = skosTree[skosTree.length - 2];
                                        if (skosTree.length > 1) {
                                            setSkosTree((s) => s.slice(0, -1));
                                            hierarchicalTaxonomyFunction(skos.uri, skos.title);
                                        }
                                    }}>
                                    {intl.formatMessage({ id: 'GLOBAL_BACK_PREVIOUS_SECTION' })}
                                </JRCButton>
                            </Components.NoResult>
                        )}
                    </Components.InputSelectCheckboxWrapper>
                </Components.ModalContentInputSelectList>
            </Components.InputSelectHierarchySearch>
        </JRCModal>
    );
};

export default SelectHierarchicalTreeModal;
